export const TICK_RATE = 60;
export const FIXED_DT = 1 / TICK_RATE;
export const INTERP_BUFFER = 100;
export const SNAPSHOT_SEND_RATE = 20;
export const INPUT_SEND_RATE = 60;
export const MAX_SNAPSHOTS = 64;
export const MAX_PLAYERS = 16;
export const MAX_INPUT_SEQ = 65535;

export const MAP_HALF = 50;
export const PLAYER_HEIGHT = 1.82;
export const CROUCH_HEIGHT = 1.12;
export const PLAYER_RADIUS = 0.42;
export const EYE_HEIGHT = 1.62;
export const CROUCH_EYE_HEIGHT = 0.92;

export const GRAVITY = 28;
export const JUMP_FORCE = 9.2;
export const DOUBLE_JUMP_FORCE = 7.0;
export const MOVE_ACCEL = 62;
export const AIR_ACCEL = 24;
export const GROUND_FRICTION = 12;
export const AIR_DRAG = 0.15;
export const MAX_GROUND_SPEED = 8.8;
export const MAX_AIR_SPEED = 10.2;
export const SPRINT_MULTIPLIER = 1.35;
export const CROUCH_SPEED = 4.0;
export const SLIDE_INITIAL_BOOST = 1.6;
export const SLIDE_DURATION = 0.45;
export const SLIDE_COOLDOWN = 0.6;
export const WALL_SLIDE_SPEED = 2.0;
export const WALL_JUMP_FORCE = 7.5;
export const WALL_JUMP_UP = 8.0;

export const BASE_HEALTH = 100;
export const BASE_ARMOR = 0;
export const MAX_HEALTH = 100;
export const MAX_ARMOR = 100;

export const HEADSHOT_MULT = 1.5;
export const BODYSHOT_MULT = 1.0;
export const LIMBSHOT_MULT = 0.8;

export const XP_PER_KILL = 50;
export const XP_PER_DEATH = 10;
export const XP_PER_WIN = 200;
export const XP_PER_MATCH = 30;
export const XP_PER_LEVEL_BASE = 1000;
export const XP_LEVEL_MULTIPLIER = 1.15;

export const TR_DAILY_REWARD = 50;
export const TR_WEEKLY_REWARD = 500;
export const TR_SEASONAL_REWARD = 2500;

export const REPORT_COOLDOWN = 300000;
export const CHAT_COOLDOWN = 1000;
export const MAX_MESSAGE_LENGTH = 200;

export const WEAPON_DEFINITIONS = {
  pistol: {
    id: 'pistol',
    name: 'Sidearm',
    slot: 'secondary' as const,
    damage: 28,
    fireRate: 0.18,
    reloadTime: 1.2,
    magazineSize: 15,
    maxAmmo: 60,
    range: 40,
    spread: 0.04,
    recoil: 0.03,
    moveSpeedPenalty: 0.02,
    adsSpeed: 0.15,
    model: 'weapon_pistol',
    sound: 'snd_pistol',
    icon: 'icon_pistol',
  },
  smg: {
    id: 'smg',
    name: 'Vector',
    slot: 'primary' as const,
    damage: 22,
    fireRate: 0.075,
    reloadTime: 1.8,
    magazineSize: 35,
    maxAmmo: 175,
    range: 30,
    spread: 0.06,
    recoil: 0.04,
    moveSpeedPenalty: 0.04,
    adsSpeed: 0.18,
    model: 'weapon_smg',
    sound: 'snd_smg',
    icon: 'icon_smg',
  },
  rifle: {
    id: 'rifle',
    name: 'Pulse',
    slot: 'primary' as const,
    damage: 32,
    fireRate: 0.1,
    reloadTime: 2.0,
    magazineSize: 30,
    maxAmmo: 120,
    range: 60,
    spread: 0.03,
    recoil: 0.05,
    moveSpeedPenalty: 0.06,
    adsSpeed: 0.2,
    model: 'weapon_rifle',
    sound: 'snd_rifle',
    icon: 'icon_rifle',
  },
  shotgun: {
    id: 'shotgun',
    name: 'Breach',
    slot: 'primary' as const,
    damage: 12,
    fireRate: 0.7,
    reloadTime: 2.4,
    magazineSize: 8,
    maxAmmo: 32,
    range: 15,
    spread: 0.15,
    recoil: 0.1,
    moveSpeedPenalty: 0.08,
    adsSpeed: 0.22,
    model: 'weapon_shotgun',
    sound: 'snd_shotgun',
    icon: 'icon_shotgun',
  },
  sniper: {
    id: 'sniper',
    name: 'Rail',
    slot: 'primary' as const,
    damage: 85,
    fireRate: 1.2,
    reloadTime: 2.8,
    magazineSize: 5,
    maxAmmo: 20,
    range: 120,
    spread: 0.005,
    recoil: 0.12,
    moveSpeedPenalty: 0.1,
    adsSpeed: 0.3,
    model: 'weapon_sniper',
    sound: 'snd_sniper',
    icon: 'icon_sniper',
  },
  melee: {
    id: 'melee',
    name: 'Blade',
    slot: 'melee' as const,
    damage: 55,
    fireRate: 0.5,
    reloadTime: 0,
    magazineSize: 0,
    maxAmmo: 0,
    range: 3.5,
    spread: 0,
    recoil: 0,
    moveSpeedPenalty: 0,
    adsSpeed: 0,
    model: 'weapon_melee',
    sound: 'snd_melee',
    icon: 'icon_melee',
  },
} as const;

export const COLORS = {
  cyan: '#00e5ff',
  blue: '#2979ff',
  electric: '#536dfe',
  purple: '#7c4dff',
  green: '#00e676',
  amber: '#ffc400',
  red: '#ff1744',
  pink: '#ff4081',
} as const;

export const RARITY_COLORS: Record<string, string> = {
  common: '#9e9e9e',
  uncommon: '#4caf50',
  rare: '#2196f3',
  epic: '#9c27b0',
  legendary: '#ff9800',
  mythic: '#ff1744',
};

export const MAPS = {
  arena: {
    id: 'arena',
    name: 'Shatterpoint',
    gameModes: ['ffa', 'team', 'capture'],
    size: 100,
  },
  parkour: {
    id: 'parkour',
    name: 'Ascension',
    gameModes: ['parkour'],
    size: 100,
  },
  infection: {
    id: 'infection',
    name: 'Outbreak',
    gameModes: ['infection', 'survival'],
    size: 80,
  },
} as const;

export const REGIONS = ['na-east', 'na-west', 'eu-central', 'asia-east', 'oceania'] as const;

export const MAX_UPLOAD_SIZE = 10 * 1024 * 1024;
export const MAX_MAP_SIZE = 50 * 1024 * 1024;
